import {
  EmptyInput,
  InputGroup,
  InputRow,
  InputGroupBreak,
  RadioYesNoInput,
  ModelSelect,
  ModelDateDMY,
  useWarningAlert,
  useModelFormUpdate,
  ModelType,
  AlertModalTypeEnum,
  RadioListInput,
  ModelNewNumericInput,
  flattenErrorTypeArray,
} from '@equifinance/shared';
import styled from 'styled-components';
import Section from '../../components/Section';
import { AccountTitle } from '../../components/AccountTitle';
import {
  ConstructionType,
  PropertyType,
  PropertyValueType,
  Tenure,
} from '../../../../contexts/account/Account';
import { Stages } from '../../../../types/Stages';
import { useAccountContext } from '../../../../contexts';
import { HighlightInputGroup } from '../../components/HighlightInputGroup';

const PropertyValueContainer = styled.div<{transparent: boolean}>` ${({ theme, transparent = false }) => `
  background: ${transparent ? 'none' : theme.colours.neutral[9]};
  border-radius: ${theme.borderRadius[2]};
`}`;

const PropertyValueLine = styled.div` ${({ theme }) => `
  height: 2px;
  background: ${theme.colours.neutral[7]};
`}`;

export default function PropertyDetails() {
  const { account } = useAccountContext();

  const {
    stage,
    property,
    securityAddress,
    errors,
    warnings,
  } = account;

  const warningAlert = useWarningAlert();

  const update = useModelFormUpdate();

  const propertyWarnings = flattenErrorTypeArray(warnings?.[property?.id] ?? []);

  const showChangeExCouncilModal = (model: ModelType) => {
    if (property?.isExCouncil) {
      warningAlert({
        onConfirm: () => {
          update({
            'property.isExCouncil': model['property.isExCouncil'],
          });
        },
        title: 'Confirmation',
        text: 'This property has been marked as ex-council. If you change the selection to “No”, all data which has been provided in relation to it being ex-council will be lost.',
        content: 'Do you want to continue?',
        confirmLabel: 'Yes',
        type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
      });
    } else {
      update({
        'property.isExCouncil': model['property.isExCouncil'],
      });
    }
  };

  return (
    <Section
      id={property?.id}
      title="Property details"
      level={1}
      errors={errors}
      warnings={warnings}
      titleIcon={<></>}
    >
      <>
        <PropertyValueContainer transparent={property?.passesValuationCriteria === null}>
          {property?.passesValuationCriteria !== null && (
            <>
              <InputGroup>
                <AccountTitle>Property value</AccountTitle>
              </InputGroup>

              <PropertyValueLine />
            </>
          )}

          <InputGroup>
            <InputRow>
              {property?.passesValuationCriteria === true && (
                <ModelNewNumericInput
                  label={<>Based on AVM</>}
                  field="property.propertyValue"
                  prefix="£"
                  isFixedDecimal
                  required
                  disabled
                  maxCharacters={10}
                />
              )}

              {property?.passesValuationCriteria === null ? (
                <ModelNewNumericInput
                  label="Property value"
                  field="property.estimatedPropertyValue"
                  prefix="£"
                  isFixedDecimal
                  required
                  maxCharacters={10}
                />
              ) : (
                <ModelNewNumericInput
                  label="Based on Applicant's estimate"
                  field="property.estimatedPropertyValue"
                  prefix="£"
                  isFixedDecimal
                  required
                  maxCharacters={10}
                />
              )}

              {property?.passesValuationCriteria !== true && (
                <EmptyInput />
              )}
            </InputRow>

          </InputGroup>

          {property?.passesValuationCriteria === null && propertyWarnings.length === 0 && (
            <InputGroupBreak />
          )}

          {property?.passesValuationCriteria === true && (
            <>
              <InputGroupBreak />

              <InputGroup>
                <InputRow>
                  <RadioListInput
                    label="Which property value would you like to continue with?"
                    field="property.propertyValueType"
                    options={PropertyValueType}
                    labelOverride={{
                      'BasedOnAvm': 'AVM',
                      'BaseOnEstimateOfApplicant': 'Applicant\'s estimate',
                    }}
                    required
                  />
                  <EmptyInput />
                </InputRow>
              </InputGroup>
            </>
          )}

        </PropertyValueContainer>

        <InputGroup>
          <InputRow>
            <RadioYesNoInput
              label={<>Was the property bought through either Shared Ownership or Help to Buy?</>}
              field="property.wasBoughtThroughSharedOwnershipOrHelpToBuy"
              required
            />

            {property?.wasBoughtThroughSharedOwnershipOrHelpToBuy === true ? (
              <RadioYesNoInput
                label="Are you purchasing the full share with this loan advance?"
                field="property.purchasingTheFullShareWithThisLoanAdvance"
                required
              />
            ) : <EmptyInput />}

          </InputRow>
        </InputGroup>

        <InputGroupBreak />

        <InputGroup>
          <InputRow>
            <RadioYesNoInput
              label="Is the property ex-council?"
              field="property.isExCouncil"
              required
              updateOverride={showChangeExCouncilModal}
            />
            <EmptyInput />
          </InputRow>
        </InputGroup>

        {property?.isExCouncil && (
          <HighlightInputGroup>
            <InputGroup>
              <InputRow>
                <RadioYesNoInput
                  label={<>RTB purchase?</>}
                  field="property.isRtbPurchase"
                  required
                />
                <EmptyInput />
              </InputRow>
            </InputGroup>

            {property?.isRtbPurchase && (
            <>
              <InputGroupBreak />

              <InputGroup>
                <InputRow>
                  <ModelNewNumericInput
                    label={<>Amount of RTB discount</>}
                    field="property.amountOfDiscount"
                    prefix="£"
                    isFixedDecimal
                    required
                    maxCharacters={10}
                  />
                  <ModelNewNumericInput
                    label="Purchase Price"
                    field="property.purchasePrice"
                    prefix="£"
                    isFixedDecimal
                    required
                    maxCharacters={10}
                  />
                </InputRow>
              </InputGroup>

              <InputGroupBreak />

              <InputGroup>
                <InputRow>
                  <ModelDateDMY
                    label="Date of purchase"
                    field="property.dateOfPurchase"
                    required
                  />
                  <EmptyInput />
                </InputRow>
              </InputGroup>
            </>
            )}
          </HighlightInputGroup>
        )}

        <InputGroup>
          <InputRow>
            <ModelSelect
              label="Tenure"
              field="property.tenure"
              options={Tenure}
              disabled={securityAddress?.country === 'GB-SCT'}
              required
            />
            <EmptyInput />
          </InputRow>
        </InputGroup>

        <InputGroupBreak />

        {property?.tenure === 'Leasehold' && (
          <HighlightInputGroup>
            <InputGroup>
              <InputRow>
                <ModelNewNumericInput
                  label="Remaining lease"
                  field="property.remainingLease"
                  suffix="year(s)"
                  required
                  integer
                  noGroupingSeperator
                />
                { stage !== Stages.CreditDecision ? (
                  <>
                    <ModelNewNumericInput
                      label="Monthly ground rent"
                      field="property.monthlyGroundRent"
                      prefix="£"
                      isFixedDecimal
                      required
                      maxCharacters={10}
                    />
                    <ModelNewNumericInput
                      label="Monthly service charge"
                      field="property.monthlyServiceCharge"
                      prefix="£"
                      isFixedDecimal
                      required
                      maxCharacters={10}
                    />
                  </>
                ) : (
                  <EmptyInput />
                )}

              </InputRow>
            </InputGroup>
          </HighlightInputGroup>
        )}

        <InputGroup>
          <InputRow>
            <ModelSelect
              label="Property type"
              field="property.propertyType"
              options={PropertyType}
              required
            />
            <EmptyInput />
          </InputRow>
        </InputGroup>

        <InputGroupBreak />

        <InputGroup>
          <InputRow>
            <ModelNewNumericInput
              label="No. of bedrooms"
              field="property.bedrooms"
              required
              integer
              noGroupingSeperator
            />
            <ModelNewNumericInput
              label="No. of living rooms"
              field="property.livingRooms"
              integer
              noGroupingSeperator
            />
          </InputRow>
        </InputGroup>

        <InputGroupBreak />

        {[
          'ConvertedFlatMaisonette',
          'PurposeBuiltFlatMaisonette',
        ].includes(property?.propertyType) && (
          <>
            <InputGroup>
              <InputRow>
                <ModelNewNumericInput
                  label="No. of floors in the block"
                  field="property.floorsInBlock"
                  required
                  integer
                  noGroupingSeperator
                />
                <RadioYesNoInput
                  label="Is the property above commercial premises?"
                  field="property.isPropertyAboveCommercialPremises"
                  required
                />
              </InputRow>
            </InputGroup>

            <InputGroupBreak />
          </>
        )}

        <InputGroup>
          <InputRow>
            <ModelSelect
              label="Construction type"
              field="property.constructionType"
              options={ConstructionType}
              required
            />
            <RadioYesNoInput
              label="Parking?"
              field="property.isParking"
            />
          </InputRow>
        </InputGroup>
      </>
    </Section>
  );
}
